import Vue from 'vue';
import {ActionTree, GetterTree, Module, MutationTree} from 'vuex';
import {RootState} from '../RootState';
import axios from 'axios';
import {settings} from '@/plugins/settings';
import {IErrors} from '@/model/IErrors';
import responseHandler from '@/plugins/errorHandling';
import {IInvoice, IInvoiceOrder, IShortInvoice} from '@/model/Invoice/invoiceInterfaces';
import {
  PAGINATION_FIRST_PAGE,
  PAGINATION_DEFAULT_LAST_PAGE,
  PAGINATION_DEFAULT_TOTAL,
  PAGINATION_DEFAULT_PER_PAGE,
} from '@/constants/pagination';

interface InvoiceState {
  invoice: IInvoice | null;
  invoiceList: IShortInvoice[];
  ordersForInvoicing: IInvoiceOrder[];
  currentPage: number;
  lastPage: number;
  total: number;
  perPage: number;
  errors: IErrors;
}

const state: InvoiceState = {
  invoice: null,
  invoiceList: [],
  ordersForInvoicing: [],
  currentPage: PAGINATION_FIRST_PAGE,
  lastPage: PAGINATION_DEFAULT_LAST_PAGE,
  total: PAGINATION_DEFAULT_TOTAL,
  perPage: PAGINATION_DEFAULT_PER_PAGE,
  errors: {},
};

const getters: GetterTree<InvoiceState, RootState> = {
  invoice: (state) => state.invoice,
  invoiceList: (state) => state.invoiceList,
  ordersForInvoicing: (state) => state.ordersForInvoicing,
  currentPage: (state) => state.currentPage,
  lastPage: (state) => state.lastPage,
  total: (state) => state.total,
  perPage: (state) => state.perPage,
  errors: (state) => state.errors,
};

const actions: ActionTree<InvoiceState, RootState> = {
  fetchInvoices: ({commit}, filters: any): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'invoices',
      params: filters,
    }),
    commit,
    'invoicesLoaded'
  ),

  fetchInvoice: ({commit}, invoiceId: number): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'invoices/' + invoiceId,
    }),
    commit,
    'invoiceLoaded'
  ),

  createInvoice: ({commit}, orderIds: number[]): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'invoices',
      method: 'POST',
      data: {order_ids: orderIds},
    }),
    commit,
    'invoiceLoaded'
  ),

  updateInvoice: ({commit}, {invoiceId, orderIds}: {invoiceId: number; orderIds: number[]}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'invoices/' + invoiceId,
      method: 'PATCH',
      data: {order_ids: orderIds},
    }),
    commit,
    'invoiceLoaded'
  ),

  deleteInvoice: ({commit}, invoiceId: number): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'invoices/' + invoiceId,
      method: 'DELETE',
    }),
    commit,
    'invoiceDeleted'
  ),

  sendEmail: ({commit}, invoiceId: number): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'invoices/' + invoiceId + '/email',
      method: 'POST',
    }),
    commit,
    'invoiceLoaded'
  ),

  addPayment: ({commit}, {invoiceId, amount}: {invoiceId: number; amount: string}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'invoices/' + invoiceId + '/payments',
      method: 'POST',
      data: {amount},
    }),
    commit,
    'invoiceLoaded'
  ),

  fetchOrdersForInvoicing: ({commit}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/for-invoicing',
    }),
    commit,
    'ordersForInvoicingLoaded'
  ),

  errors: ({commit}, errors: IErrors) => {
    commit('errors', errors);
  },
};

const mutations: MutationTree<InvoiceState> = {
  invoicesLoaded(state, payload: any) {
    if (!payload) {
      state.invoiceList = [];
      return;
    }
    state.invoiceList = Array.isArray(payload.data) ? payload.data : [];
    state.currentPage = payload.current_page || PAGINATION_FIRST_PAGE;
    state.lastPage = payload.last_page || PAGINATION_DEFAULT_LAST_PAGE;
    state.total = payload.total || PAGINATION_DEFAULT_TOTAL;
    state.perPage = payload.per_page || PAGINATION_DEFAULT_PER_PAGE;
  },
  invoiceLoaded(state, payload: IInvoice | null) {
    state.invoice = payload;
    if (payload) {
      const idx = state.invoiceList.findIndex((i) => i.id === payload.id);
      if (idx !== -1) {
        Vue.set(state.invoiceList, idx, payload);
      }
    }
  },
  invoiceDeleted(state) {
    state.invoice = null;
  },
  ordersForInvoicingLoaded(state, payload: any) {
    state.ordersForInvoicing = payload && Array.isArray(payload.data) ? payload.data : [];
  },
  errors(state, payload: IErrors) {
    state.errors = payload;
  },
  cleanError(state, index: string) {
    Vue.delete(state.errors, index);
  },
};

const namespaced: boolean = true;

export const invoiceModule: Module<InvoiceState, RootState> = {
  namespaced,
  state,
  getters,
  actions,
  mutations,
};
